/**
 * Weave module - Process embed directives and variable substitutions in markdown
 *
 * This module ports the bash weave.sh script to TypeScript for use in both
 * Node.js and browser environments.
 */

import type { WeaveOptions, WeaveResult, ComponentResolver } from './types.js';
import { parseFrontmatter, parseEmbedDirective, variablesToRecord } from './parser.js';
import { substituteVariables, transformTangleTargets, findUnresolvedVariables, cleanForExport, stripTangleMarkers } from './variables.js';
import { processNumberedHeadings } from './headings.js';
import { expandWikilinks } from '../wikilinks.js';

export { parseFrontmatter, parseEmbedDirective, variablesToRecord } from './parser.js';
export { substituteVariables, transformTangleTargets, findUnresolvedVariables, cleanForExport, stripTangleMarkers } from './variables.js';
export { processNumberedHeadings } from './headings.js';
export type * from './types.js';

const DEFAULT_MAX_DEPTH = 10;

/**
 * Process a file/content recursively, expanding embeds
 *
 * @param content - The content to process
 * @param depth - Current recursion depth
 * @param maxDepth - Maximum allowed depth
 * @param variables - Variables for substitution
 * @param componentResolver - Function to resolve component content
 * @param warnings - Array to collect warnings
 * @returns Processed content with embeds expanded
 */
function processContent(
  content: string,
  depth: number,
  maxDepth: number,
  variables: Record<string, string>,
  componentResolver: ComponentResolver,
  warnings: string[]
): string {
  if (depth > maxDepth) {
    throw new Error(`Maximum embed depth (${maxDepth}) exceeded. Possible circular reference?`);
  }

  const lines = content.split('\n');
  const outputLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineNumber = i + 1;

    // Check for embed directive
    const embed = parseEmbedDirective(line, lineNumber);

    if (embed) {
      // Resolve component content
      const componentContent = componentResolver(embed.component);

      if (componentContent === null) {
        throw new Error(`Component not found: ${embed.component} (line ${lineNumber})`);
      }

      // Recursively process the component
      let processedComponent = processContent(
        componentContent,
        depth + 1,
        maxDepth,
        variables,
        componentResolver,
        warnings
      );

      // Apply per-embed params: rewrite the component's `{{NAME}}` placeholders
      // that this directive maps to a fixed value (PORT=8080) or remaps to a
      // parent variable (HOST={{SERVER_HOST}}). This is a textual placeholder
      // rewrite, so remap targets like `{{SERVER_HOST}}` survive to be resolved
      // by the normal (or tangle) substitution pass. Applied after recursion so
      // params cascade over the whole embedded subtree; inner params (already
      // applied) take precedence over the outer embed's.
      if (embed.params) {
        const { result } = substituteVariables(processedComponent, embed.params);
        processedComponent = result;
      }

      // Apply variable substitution to target (e.g., {{INIT_SCRIPT}} -> 1.init.sh)
      let targetScript = embed.target;
      if (targetScript) {
        const { result } = substituteVariables(targetScript, variables);
        targetScript = result;
      }

      // Transform tangle targets based on the target parameter
      processedComponent = transformTangleTargets(processedComponent, targetScript);

      // Add component content (without trailing newline, we add one below)
      outputLines.push(processedComponent);
    } else {
      outputLines.push(line);
    }
  }

  return outputLines.join('\n');
}

/**
 * Weave markdown content - process embeds and substitute variables
 *
 * This is the main entry point for the weave module.
 *
 * @param content - The source markdown content
 * @param options - Weave options
 * @returns WeaveResult with output and status
 */
export function weave(content: string, options: WeaveOptions = {}): WeaveResult {
  const {
    variables: overrideVariables = {},
    components = {},
    maxDepth = DEFAULT_MAX_DEPTH,
    skipVariableSubstitution = false,
    sourcePath,
    allPaths,
  } = options;

  const warnings: string[] = [];

  try {
    // Step 1: Parse and remove frontmatter
    const { variables: frontmatterVars, contentWithoutFrontmatter } = parseFrontmatter(content);

    // Step 2: Merge variables (frontmatter + overrides, overrides take precedence)
    const variables: Record<string, string> = {
      ...variablesToRecord(frontmatterVars),
      ...overrideVariables,
    };

    // Step 3: Create component resolver from provided components map
    const componentResolver: ComponentResolver = (componentPath: string) => {
      // Try exact match first
      if (componentPath in components) {
        return components[componentPath];
      }
      // Try without leading path for _components/ convention
      const baseName = componentPath.split('/').pop();
      if (baseName && baseName in components) {
        return components[baseName];
      }
      return null;
    };

    // Step 4: Process embeds recursively
    let output = processContent(
      contentWithoutFrontmatter,
      0,
      maxDepth,
      variables,
      componentResolver,
      warnings
    );

    // Step 5: Process numbered headings (## 0. syntax)
    output = processNumberedHeadings(output);

    // Step 5b: Expand `[[wikilinks]]` to standard markdown links
    output = expandWikilinks(output, { sourcePath, allPaths });

    // Step 6: Apply variable substitutions (unless skipped for template preservation)
    let unresolvedVariables: string[] = [];
    if (!skipVariableSubstitution) {
      const { result: substitutedOutput } = substituteVariables(output, variables);
      output = substitutedOutput;

      // Step 7: Check for remaining unresolved variables and add warnings
      unresolvedVariables = findUnresolvedVariables(output);
      if (unresolvedVariables.length > 0) {
        warnings.push(`Unresolved variables: ${unresolvedVariables.join(', ')}`);
      }
    }

    // Strip leading and trailing blank lines so the rendered preview never has
    // an empty gap at the top (e.g. left behind by a stripped vars block) or a
    // dangling blank line at the bottom.
    output = output.replace(/^\s*\n/, '').replace(/\s+$/, '');

    return {
      output,
      success: true,
      warnings,
      unresolvedVariables,
    };
  } catch (error) {
    return {
      output: '',
      success: false,
      warnings,
      unresolvedVariables: [],
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Weave with file system access (Node.js only)
 *
 * This variant accepts a component resolver that can access the file system.
 * Used by the server to load components from disk.
 *
 * @param content - The source markdown content
 * @param componentResolver - Function to resolve component content from file system
 * @param options - Weave options (components option is ignored)
 * @returns WeaveResult with output and status
 */
export function weaveWithResolver(
  content: string,
  componentResolver: ComponentResolver,
  options: Omit<WeaveOptions, 'components'> = {}
): WeaveResult {
  const {
    variables: overrideVariables = {},
    maxDepth = DEFAULT_MAX_DEPTH,
    sourcePath,
    allPaths,
  } = options;

  const warnings: string[] = [];

  try {
    // Step 1: Parse and remove frontmatter
    const { variables: frontmatterVars, contentWithoutFrontmatter } = parseFrontmatter(content);

    // Step 2: Merge variables
    const variables: Record<string, string> = {
      ...variablesToRecord(frontmatterVars),
      ...overrideVariables,
    };

    // Step 3: Process embeds recursively
    let output = processContent(
      contentWithoutFrontmatter,
      0,
      maxDepth,
      variables,
      componentResolver,
      warnings
    );

    // Step 4: Process numbered headings (## 0. syntax)
    output = processNumberedHeadings(output);

    // Step 4b: Expand `[[wikilinks]]` to standard markdown links
    output = expandWikilinks(output, { sourcePath, allPaths });

    // Step 5: Apply variable substitutions
    const { result: substitutedOutput } = substituteVariables(output, variables);
    output = substitutedOutput;

    // Step 6: Check for unresolved variables
    const unresolvedVariables = findUnresolvedVariables(output);
    if (unresolvedVariables.length > 0) {
      warnings.push(`Unresolved variables: ${unresolvedVariables.join(', ')}`);
    }

    // Strip leading and trailing blank lines (see comment in weave()).
    output = output.replace(/^\s*\n/, '').replace(/\s+$/, '');

    return {
      output,
      success: true,
      warnings,
      unresolvedVariables,
    };
  } catch (error) {
    return {
      output: '',
      success: false,
      warnings,
      unresolvedVariables: [],
      error: error instanceof Error ? error.message : String(error),
    };
  }
}
